import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  Close,
  Save,
  Business,
  Inventory,
  AttachMoney,
  TrendingUp,
  CheckCircle,
  Lock,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { itemSuppliersService, CreateItemSupplierDto, UpdateItemSupplierDto, ItemSupplier } from '../../services/itemSuppliers.service';
import { itemsService } from '../../services/items.service';
import suppliersService from '../../services/suppliers.service';
import unitsService from '../../services/units.service';

const translations = {
  ar: {
    title: 'تعيين مورد لصنف',
    editTitle: 'تعديل تعيين المورد',
    item: 'الصنف',
    supplier: 'المورد',
    unit: 'الوحدة',
    price: 'سعر المورد',
    isPreferred: 'مورد مفضل',
    isActive: 'نشط',
    notes: 'ملاحظات',
    save: 'حفظ',
    cancel: 'إلغاء',
    selectItem: 'اختر الصنف',
    selectSupplier: 'اختر المورد',
    selectUnit: 'اختر الوحدة',
    saveSuccess: 'تم الحفظ بنجاح',
    saveError: 'فشل الحفظ',
    loadError: 'فشل تحميل البيانات',
    itemRequired: 'الصنف مطلوب',
    supplierRequired: 'المورد مطلوب',
    unitRequired: 'الوحدة مطلوبة',
    priceRequired: 'السعر مطلوب',
    priceInvalid: 'السعر يجب أن يكون أكبر من صفر',
    alreadyExists: 'هذا المورد مُعيّن بالفعل لهذا الصنف بنفس الوحدة',
    itemPolicy: 'سياسة الصنف',
    supplierRequiredForItem: 'مورد إجباري',
    priceControlled: 'سعر محدد',
    currentPrice: 'السعر الحالي',
    lastPurchase: 'آخر شراء',
  },
  en: {
    title: 'Assign Supplier to Item',
    editTitle: 'Edit Supplier Assignment',
    item: 'Item',
    supplier: 'Supplier',
    unit: 'Unit',
    price: 'Supplier Price',
    isPreferred: 'Preferred Supplier',
    isActive: 'Active',
    notes: 'Notes',
    save: 'Save',
    cancel: 'Cancel',
    selectItem: 'Select Item',
    selectSupplier: 'Select Supplier',
    selectUnit: 'Select Unit',
    saveSuccess: 'Saved successfully',
    saveError: 'Failed to save',
    loadError: 'Failed to load data',
    itemRequired: 'Item is required',
    supplierRequired: 'Supplier is required',
    unitRequired: 'Unit is required',
    priceRequired: 'Price is required',
    priceInvalid: 'Price must be greater than zero',
    alreadyExists: 'This supplier is already assigned to this item with same unit',
    itemPolicy: 'Item Policy',
    supplierRequiredForItem: 'Supplier Required',
    priceControlled: 'Price Controlled',
    currentPrice: 'Current Price',
    lastPurchase: 'Last Purchase',
  },
};

interface ItemSupplierDialogProps {
  open: boolean;
  onClose: () => void;
  itemSupplier?: ItemSupplier | null;
  onSaved?: () => void;
}

export default function ItemSupplierDialog({
  open,
  onClose,
  itemSupplier,
  onSaved,
}: ItemSupplierDialogProps) {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [items, setItems] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    itemId: null as number | null,
    supplierId: null as number | null,
    unitId: null as number | null,
    supplierPrice: 0,
    isPreferred: false,
    isActive: true,
    notes: '',
    isSupplierRequired: false,
    isPriceControlled: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  useEffect(() => {
    if (itemSupplier) {
      setFormData({
        itemId: itemSupplier.itemId,
        supplierId: itemSupplier.supplierId,
        unitId: itemSupplier.unitId,
        supplierPrice: itemSupplier.supplierPrice,
        isPreferred: itemSupplier.isPreferred,
        isActive: itemSupplier.isActive,
        notes: itemSupplier.notes || '',
        isSupplierRequired: false,
        isPriceControlled: false,
      });
      
      // Load item policy
      if (itemSupplier.itemId) {
        itemSuppliersService.getItemPolicy(itemSupplier.itemId)
          .then(policy => {
            if (policy) {
              setFormData(prev => ({
                ...prev,
                isSupplierRequired: policy.isSupplierRequired,
                isPriceControlled: policy.isPriceControlled,
              }));
            }
          })
          .catch(() => {});
      }
    } else {
      setFormData({
        itemId: null,
        supplierId: null,
        unitId: null,
        supplierPrice: 0,
        isPreferred: false,
        isActive: true,
        notes: '',
        isSupplierRequired: false,
        isPriceControlled: false,
      });
    }
    setErrors({});
  }, [itemSupplier, open]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsData, suppliersData, unitsData] = await Promise.all([
        itemsService.getAll(),
        suppliersService.getAll(),
        unitsService.getAll(),
      ]);
      setItems(itemsData);
      setSuppliers(suppliersData);
      setUnits(unitsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.itemId) newErrors.itemId = t.itemRequired;
    if (!formData.supplierId) newErrors.supplierId = t.supplierRequired;
    if (!formData.unitId) newErrors.unitId = t.unitRequired;
    if (!formData.supplierPrice || formData.supplierPrice <= 0) {
      newErrors.supplierPrice = t.priceInvalid;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      if (itemSupplier) {
        // Update
        const updateData: UpdateItemSupplierDto = {
          itemSupplierId: itemSupplier.itemSupplierId,
          supplierPrice: formData.supplierPrice,
          isPreferred: formData.isPreferred,
          isActive: formData.isActive,
          notes: formData.notes || '',
        };
        await itemSuppliersService.update(itemSupplier.itemSupplierId, updateData);
      } else {
        // Create
        const createData: CreateItemSupplierDto = {
          itemId: formData.itemId!,
          supplierId: formData.supplierId!,
          unitId: formData.unitId!,
          supplierPrice: formData.supplierPrice,
          isPreferred: formData.isPreferred,
          isActive: formData.isActive,
          notes: formData.notes,
        };
        await itemSuppliersService.create(createData);
      }

      // Update item policy
      if (formData.itemId) {
        try {
          await itemSuppliersService.updateItemPolicy({
            itemId: formData.itemId,
            isSupplierRequired: formData.isSupplierRequired,
            isPriceControlled: formData.isPriceControlled,
          });
          
        } catch (policyError) {
          console.error('❌ Failed to update policy:', policyError);
        }
      }

      toast.success(t.saveSuccess);
      onSaved?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to save:', error);
      
      if (error.response?.status === 409) {
        toast.error(t.alreadyExists);
      } else {
        toast.error(t.saveError);
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedItem = items.find(i => i.itemId === formData.itemId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Business sx={{ fontSize: 32 }} />
          <Typography variant="h6" fontWeight="bold">
            {itemSupplier ? t.editTitle : t.title}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Item Policy Alert */}
            {selectedItem && (selectedItem.isSupplierRequired || selectedItem.isPriceControlled) && (
              <Alert
                severity="info"
                icon={<CheckCircle />}
                sx={{ borderRadius: 2 }}
              >
                <Typography variant="subtitle2" fontWeight="600" mb={1}>
                  {t.itemPolicy}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {selectedItem.isSupplierRequired && (
                    <Chip
                      label={t.supplierRequiredForItem}
                      color="primary"
                      size="small"
                      icon={<Business />}
                    />
                  )}
                  {selectedItem.isPriceControlled && (
                    <Chip
                      label={t.priceControlled}
                      color="warning"
                      size="small"
                      icon={<AttachMoney />}
                    />
                  )}
                </Stack>
              </Alert>
            )}

            {/* Item */}
            <Autocomplete
              options={items}
              getOptionLabel={(option) =>
                `${option.itemCode} - ${isRTL ? option.itemNameAr : option.itemName}`
              }
              value={items.find((i) => i.itemId === formData.itemId) || null}
              onChange={async (_, newValue) => {
                if (newValue) {
                  try {
                    const policy = await itemSuppliersService.getItemPolicy(newValue.itemId);
                    setFormData({ 
                      ...formData, 
                      itemId: newValue.itemId,
                      isSupplierRequired: policy?.isSupplierRequired || false,
                      isPriceControlled: policy?.isPriceControlled || false,
                    });
                  } catch {
                    setFormData({ ...formData, itemId: newValue.itemId });
                  }
                } else {
                  setFormData({ ...formData, itemId: null });
                }
                setErrors({ ...errors, itemId: '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t.item}
                  error={!!errors.itemId}
                  helperText={errors.itemId}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Inventory color="primary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Supplier */}
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option) =>
                `${option.supplierCode} - ${isRTL ? option.supplierNameAr : option.supplierName}`
              }
              value={suppliers.find((s) => s.supplierId === formData.supplierId) || null}
              onChange={(_, newValue) => {
                setFormData({ ...formData, supplierId: newValue?.supplierId || null });
                setErrors({ ...errors, supplierId: '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t.supplier}
                  error={!!errors.supplierId}
                  helperText={errors.supplierId}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Business color="error" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Unit */}
            <Autocomplete
              options={units}
              getOptionLabel={(option) => isRTL ? option.unitNameAr : option.unitName}
              value={units.find((u) => u.unitId === formData.unitId) || null}
              onChange={(_, newValue) => {
                setFormData({ ...formData, unitId: newValue?.unitId || null });
                setErrors({ ...errors, unitId: '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t.unit}
                  error={!!errors.unitId}
                  helperText={errors.unitId}
                />
              )}
            />

            {/* Supplier Price */}
            <TextField
              fullWidth
              label={t.price}
              type="number"
              value={formData.supplierPrice}
              onChange={(e) => {
                setFormData({ ...formData, supplierPrice: parseFloat(e.target.value) || 0 });
                setErrors({ ...errors, supplierPrice: '' });
              }}
              error={!!errors.supplierPrice}
              helperText={errors.supplierPrice}
              inputProps={{ step: 0.001 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney color="success" />
                  </InputAdornment>
                ),
                endAdornment: <InputAdornment position="end">KD</InputAdornment>,
              }}
            />

            {/* Last Purchase Info (Edit mode only) */}
            {itemSupplier && itemSupplier.lastPurchaseDate && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>{t.lastPurchase}:</strong>{' '}
                  {new Date(itemSupplier.lastPurchaseDate).toLocaleDateString('en-GB')} -{' '}
                  {itemSupplier.lastPurchasePrice?.toFixed(3)} KD
                </Typography>
              </Alert>
            )}

            {/* Switches */}
            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPreferred}
                    onChange={(e) =>
                      setFormData({ ...formData, isPreferred: e.target.checked })
                    }
                    color="success"
                  />
                }
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TrendingUp fontSize="small" />
                    <span>{t.isPreferred}</span>
                  </Stack>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label={t.isActive}
              />
            </Stack>

            {/* Item Policy Settings */}
            {formData.itemId && (
              <Paper sx={{ p: 2, bgcolor: 'rgba(250, 112, 154, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" mb={1.5} color="primary">
                  {isRTL ? 'سياسة الصنف' : 'Item Policy'}
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isSupplierRequired}
                        onChange={(e) =>
                          setFormData({ ...formData, isSupplierRequired: e.target.checked })
                        }
                        color="error"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Business fontSize="small" color="error" />
                        <span>{isRTL ? 'مورد إجباري' : 'Supplier Required'}</span>
                      </Stack>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isPriceControlled}
                        onChange={(e) =>
                          setFormData({ ...formData, isPriceControlled: e.target.checked })
                        }
                        color="warning"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Lock fontSize="small" color="warning" />
                        <span>{isRTL ? 'سعر محدد' : 'Price Controlled'}</span>
                      </Stack>
                    }
                  />
                </Stack>
              </Paper>
            )}

            {/* Notes */}
            <TextField
              fullWidth
              label={t.notes}
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: 'rgba(0,0,0,0.02)' }}>
        <Button onClick={onClose} variant="outlined" size="large">
          {t.cancel}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="large"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          sx={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            minWidth: 150,
          }}
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}